import * as React from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useAtomValue } from "jotai";
import { textColorState } from "@/lib/store";
import { Drawer, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useRouter } from "next/router";
import styles from "./Menu.module.scss";
import { useIdleEffect } from "@/lib/defer";
import { ErrorBoundary } from "react-error-boundary";

// The menu's own code (its links, groups and icons) is a chunk of its own, loaded with it below.
const loadMenuContent = () => import("./MenuContent");
const MenuContent = React.lazy(loadMenuContent);

type Anchor = "top" | "left" | "bottom" | "right";

// Burger button + left navigation drawer. The drawer paper is transparent so the
// grouped panel (MenuContent) owns the surface, border and safe-area padding.
// Memoized (it takes no props): the header re-renders whenever the user, the theme or the scroll position
// changes, and this subtree is the largest part of it.
function SwipeableTemporaryDrawer() {
    const [state, setState] = React.useState({
        left: false,
    });

    // SwipeableDrawer keeps its contents mounted while closed, for the swipe-open gesture, so the whole
    // menu (about 190 components: every section's links and icons) was loaded and built during start-up
    // on every page, as part of the shell's long tasks on a phone. It is loaded when the browser is first
    // idle instead and built as a transition, which React renders in small slices that never block input.
    // Opening the menu before then builds it at once (the drawer is empty for the moment its code takes
    // to arrive), and from then on it stays mounted, so closing it never empties it mid-slide.
    const [contentReady, setContentReady] = React.useState(false);
    useIdleEffect(() => {
        let alive = true;
        loadMenuContent()
            .then(() => alive && React.startTransition(() => setContentReady(true)))
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, []);

    const sideBarRef = React.useRef<HTMLDivElement>(null);

    const textColor = useAtomValue(textColorState);

    const { pathname } = useRouter();
    const stockSIPage = pathname === "/s/[exchange]/[ticker]/short-interest";

    const toggleDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event && event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
            return;
        }

        if (open) setContentReady(true);
        setState({ ...state, [anchor]: open });
    };

    const closeDrawer = () => setState((prev) => ({ ...prev, left: false }));

    const paperProps = {
        sx: {
            background: "transparent",
            backgroundImage: "none",
            boxShadow: "none",
            border: "none",
            overflow: "visible",
        },
    } as const;

    // A failed chunk load (offline, or a deploy since the page loaded) leaves the drawer empty instead of
    // taking the header down with it.
    const content = contentReady && (
        <ErrorBoundary fallback={null}>
            <React.Suspense fallback={null}>
                <MenuContent onNavigate={closeDrawer} />
            </React.Suspense>
        </ErrorBoundary>
    );

    return (
        <div>
            {(["left"] as const).map((anchor) => (
                <React.Fragment key={anchor}>
                    <IconButton aria-label="Open navigation menu" onClick={toggleDrawer(anchor, true)} className={styles.menuButton}>
                        <MenuIcon style={{ color: textColor }} />
                    </IconButton>

                    {stockSIPage ? (
                        <Drawer
                            anchor={anchor}
                            open={state[anchor]}
                            onClose={toggleDrawer(anchor, false)}
                            id="menu"
                            ref={sideBarRef}
                            slotProps={{
                                paper: paperProps,
                            }}
                        >
                            {content}
                        </Drawer>
                    ) : (
                        <SwipeableDrawer
                            anchor={anchor}
                            open={state[anchor]}
                            onClose={toggleDrawer(anchor, false)}
                            onOpen={toggleDrawer(anchor, true)}
                            id="menu"
                            ref={sideBarRef}
                            slotProps={{
                                paper: paperProps,
                            }}
                        >
                            {content}
                        </SwipeableDrawer>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

export default React.memo(SwipeableTemporaryDrawer);
